import { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdobeDataLayerObject, Stage } from "../types";
import { Step, SubmitAnswersRequest } from "../interfaces";

export type AuthContextType = {
    loading: boolean;
    setLoading: React.Dispatch<boolean>;
    transactionId: string;
    dealerCompanyNumber: string;
    handleDataTemplateChange: (
        step: Step,
        interaction: "Initialized" | "pagechange",
        selectDealer: boolean | undefined,
        submitRequest: SubmitAnswersRequest | undefined
    ) => void;
};

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: React.ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(false);
    const [transactionId, setTransactionId] = useState<string>("");
    const [dealerCompanyNumber, setDealerCompanyNumber] = useState<string>("");
    const TRANSACTION_ID_PARAM_NAME = "transactionId";
    const DEALER_ID_PARAM_NAME = "DealerCompanyNumber";

    const handleDataTemplateChange = (
        step: Step,
        interaction: "Initialized" | "pagechange",
        searchDealer: boolean | undefined,
        submitRequest: SubmitAnswersRequest | undefined
    ) => {
        let temp: AdobeDataLayerObject = {
            core: {
                stagingEnvironment: process.env.REACT_APP_STAGE?.toUpperCase() as Stage,
                dataLayerVersion: "2.12",
                pageInfo: {
                    intendedCustomerDeviceType: "all",
                    language: "it",
                    market: "IT",
                    publisher: "DU",
                    pageName: "",
                    releaseDate: null,
                    version: null,
                },
                category: {
                    primaryCategory: "Digital renewal",
                    siteType: "standalone",
                    maturityLevel: "Lead",
                    contractType: "Renewal",
                    inventoryType: null,
                    processType: null,
                    productVariants: [
                        {
                            name: null,
                        },
                    ],
                    secondaryCategory: null,
                },
                attributes: {
                    journeyType: "customer-facing-product-journey",
                    transactionID: transactionId,
                    brand: process.env.REACT_APP_WEBSITE_BRAND ?? "",
                    viewChange: undefined,
                    context: null,
                    contextBrand: null,
                    faqText: null,
                    loginType: null,
                },
            },
            error: {
                errorCausingURL: null,
                errorCode: null,
                errorMessage: null,
            },
            universally: {
                numberOfProducts: null,
                nps: [
                    {
                        rating: null,
                        topic: null,
                    },
                ],
                additionalInformation: [],
            },
            product: [
                {
                    productID: null,
                    category: null,
                    name: null,
                    subname: [],
                    productVariants: [],
                    productAddons: [],
                    attributes: {
                        typeOfSale: null,
                        paymentFrequency: null,
                        currency: null,
                        recurringPayment: null,
                        duration: null,
                        durationUnit: null,
                        startDateOfContract: null,
                        endDateOfContract: null,
                        yearlyMileage: null,
                        contractMileage: null,
                        excessMileage: null,
                        mileageUnit: null,
                        paymentType: null,
                        registrationDateOwner: null,
                        preTaxSubstraction: null,
                        balloonPayment: null,
                        contractAmount: null,
                        netLoanAmount: null,
                        grossLoanAmount: null,
                        downPaymentAmount: null,
                        nominalInterestRate: null,
                        nominalInterestAmount: null,
                        effectiveInterestRate: null,
                        deductible: null,
                        specialAmount: null,
                        addOns: [
                            {
                                additionalInformation: null,
                                name: null,
                            },
                        ],
                        specialConditions: null,
                        termination: {
                            type: null,
                            reason: null,
                        },
                    },
                    vehicleModel: {
                        manufacturer: null,
                        name: null,
                        modelVariation: null,
                        year: null,
                        modelLine: null,
                        descriptionLong: null,
                        salesID: null,
                        bodyType: null,
                        category: null,
                        certifiedPreOwned: null,
                        currentMileage: null,
                        currentMileageUnit: null,
                        initialRegistrationDate: null,
                        basePrice_localCurrency: null,
                        currency: null,
                        endPrice_localCurrency: null,
                        manufacturerCode: null,
                        typeCode: null,
                        colorExterior: null,
                        typeOfUse: null,
                        priorModel: null,
                        equity: null,
                        condition: null,
                        engine: {
                            fuelType: null,
                            emission: null,
                            transmission: null,
                            enginePower: null,
                        },
                    },
                },
            ],
            dealerData: {
                companyId: null,
                companyName: null,
                regionId: null,
                KVPS: null,
                address: {
                    street: null,
                    zipCode: null,
                    city: null,
                    state: null,
                },
            },
            form: {
                type: null,
                name: null,
                fieldValues: null,
                lastTouchedField: null,
                errorFields: null,
            },
            design: { browserResolutionBreakpoint: null },
            customerData: {
                loginStatus: null,
                loggedInUserGroup: null,
                yearOfBirth: null,
                gender: null,
                ccResult: null,
                address: {
                    zipCode: null,
                },
            },
            businessData: {
                companySize: null,
            },
            filter: [
                {
                    filterName: null,
                    filterAction: null,
                    filterValue: [],
                },
            ],
            targeting: {
                pageTargetVersion: null,
                completeVisitPath: [],
            },
            video: { videoID: null, videoName: null, videoLength: null, videoViewed: null },
            dataPrivacyStatement: {
                allowPostalAds: null,
                allowPhoneAds: null,
                allowElectronicAds: null,
                allowMailAds: null,
                allowDataTransfer: null,
                allowElectronicInvoices: null,
            },
            search: {
                term: null,
                results: null,
            },
            menuTracking: {
                breadCrumbs: [],
            },
            eventInfo: [
                {
                    eventType: null,
                    eventAction: null,
                    eventTargetURL: null,
                    linkInformation: null,
                },
            ],
            event: "page",
        };
        const pageNumber =
            step === Step.MM1 ? "1" : step === Step.MM2 ? "2" : step === Step.MM3 ? "3" : step === Step.MM4 ? "4" : "";
        const pageName =
            step === Step.MM1
                ? "Vehicle preferences"
                : step === Step.MM2
                ? "Financial preferences"
                : step === Step.MM3
                ? "Vehicle additional preferences"
                : step === Step.MM4
                ? "Financial additional preferences"
                : "";
        //Todo Change data
        switch (step) {
            case Step.MM1:
            case Step.MM2:
            case Step.MM3:
            case Step.MM4:
                temp.core.pageInfo.pageName = "Match maker";
                temp.core.attributes.viewChange = `Question page ${pageNumber}: ${pageName}`;
                break;
            case Step.DEALER_SELECTION:
                temp.core.pageInfo.pageName = "Dealer selection page";
                temp.core.attributes.viewChange = !searchDealer ? "Search dealer" : "Dealer search results";
                break;
            case Step.CONTACT_DETAILS:
                temp.core.pageInfo.pageName = "Personal information page";
                temp.core.attributes.viewChange = "Personal information page";
                break;
            case Step.FINAL_CONFIRMATION:
                temp.core.pageInfo.pageName = "Summary page";
                temp.core.attributes.viewChange = "Summary page";
                break;
            case Step.FINAL_CONFIRMATION_PAGE:
                temp.core.pageInfo.pageName = "Confirmation page";
                temp.core.attributes.viewChange = "Confirmation page";
                break;
            case Step.ERROR_PAGE:
                temp.core.pageInfo.pageName = "Error page";
                temp.core.attributes.viewChange = "Error page";
                break;
            default:
                break;
        }
        if (step === Step.FINAL_CONFIRMATION_PAGE) {
            temp.eventInfo.push({
                eventAction: "Success",
                eventTargetURL: null,
                eventType: "Sale",
                linkInformation: null,
            });
        }
        if (submitRequest && submitRequest.matchMakerAnswers?.contractType) {
            if (
                submitRequest.matchMakerAnswers?.contractType &&
                submitRequest.matchMakerAnswers?.contractType.length > 0
            ) {
                temp.product = [];
                submitRequest.matchMakerAnswers?.contractType.forEach((x) => {
                    if (temp.product.filter((c) => c.name === x).length > 0) return;
                    const name =
                        x === "finance" ? "Auto Credit" : x === "financelease" ? "Finance Lease" : "Operate lease";
                    return temp.product.push({
                        productID: null,
                        category: null,
                        name: name,
                        subname: [],
                        productVariants: [],
                        productAddons: [],
                        attributes: {
                            typeOfSale: null,
                            paymentFrequency: null,
                            currency: null,
                            recurringPayment: null,
                            duration: null,
                            durationUnit: null,
                            startDateOfContract: null,
                            endDateOfContract: null,
                            yearlyMileage: null,
                            contractMileage: null,
                            excessMileage: null,
                            mileageUnit: null,
                            paymentType: null,
                            registrationDateOwner: null,
                            preTaxSubstraction: null,
                            balloonPayment: null,
                            contractAmount: null,
                            netLoanAmount: null,
                            grossLoanAmount: null,
                            downPaymentAmount: null,
                            nominalInterestRate: null,
                            nominalInterestAmount: null,
                            effectiveInterestRate: null,
                            deductible: null,
                            specialAmount: null,
                            addOns: [
                                {
                                    additionalInformation: null,
                                    name: null,
                                },
                            ],
                            specialConditions: null,
                            termination: {
                                type: null,
                                reason: null,
                            },
                        },
                        vehicleModel: {
                            manufacturer: null,
                            name: null,
                            modelVariation: null,
                            year: null,
                            modelLine: null,
                            descriptionLong: null,
                            salesID: null,
                            bodyType: null,
                            category: null,
                            certifiedPreOwned: null,
                            currentMileage: null,
                            currentMileageUnit: null,
                            initialRegistrationDate: null,
                            basePrice_localCurrency: null,
                            currency: null,
                            endPrice_localCurrency: null,
                            manufacturerCode: null,
                            typeCode: null,
                            colorExterior: null,
                            typeOfUse: null,
                            priorModel: null,
                            equity: null,
                            condition: null,
                            engine: {
                                fuelType: null,
                                emission: null,
                                transmission: null,
                                enginePower: null,
                            },
                        },
                    });
                });
            }
        }
        window.adobeDataLayer = window.adobeDataLayer || [];
        window.adobeDataLayer.push(temp);
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);

        if (!searchParams.has(TRANSACTION_ID_PARAM_NAME) || !searchParams.has(DEALER_ID_PARAM_NAME)) {
            navigate("error-page");
            return;
        }

        if (searchParams.has(TRANSACTION_ID_PARAM_NAME)) {
            setTransactionId(searchParams.get(TRANSACTION_ID_PARAM_NAME) ?? "");
        }
        if (searchParams.has(DEALER_ID_PARAM_NAME)) {
            setDealerCompanyNumber(searchParams.get(DEALER_ID_PARAM_NAME) ?? "");
        }
    }, []);

    useEffect(() => {
        async function checktransactionIdState() {
            if (transactionId) {
                setLoading(false);
                try {
                    handleDataTemplateChange(Step.MM1, "Initialized", undefined, undefined);
                } catch (e) {
                    if (process.env.NODE_ENV !== "production") {
                        console.error(`message: ${(e as any).message}, stack: ${(e as any).stack}`);
                    }
                }
            }
        }
        checktransactionIdState();
    }, [transactionId]);

    return (
        <AuthContext.Provider
            value={{
                loading,
                setLoading,
                transactionId,
                dealerCompanyNumber,
                handleDataTemplateChange,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
